import { createSettingMetadataFromGroupDefaults } from "../../settings-catalog/metadata";
import type {
  SettingDefinition,
  SettingMetadata,
  SettingRegistryMetadataInput,
  TeamSettingsSettingValueSource,
} from "../../settings-catalog/types";
import {
  OrderEditingFlatTeamSettingGroupById,
  type OrderEditingFlatTeamSettingGroupId,
} from "./groups";

type OrderEditingSettingInput = SettingRegistryMetadataInput;

function setting(metadata: OrderEditingSettingInput): OrderEditingSettingInput {
  return metadata;
}

function orderEditingGroup<
  const Group extends OrderEditingFlatTeamSettingGroupId,
  const T extends Record<string, OrderEditingSettingInput>,
>(group: Group, settings: T): { [K in keyof T]: T[K] & { group: Group } } {
  return Object.fromEntries(
    Object.entries(settings).map(([key, value]) => [key, { ...value, group }]),
  ) as { [K in keyof T]: T[K] & { group: Group } };
}

const restockValueSemantics = {
  defaultValue: true,
  falseLabel: "Do not restock",
  trueLabel: "Restock",
} as const;

const OrderEditingRegistry = {
  // TODO(Connor Ellis): these entries write the legacy global
  // `orderEditing.tools`, not the per-rule `tools` the editing rules UI shows,
  // so a catalog edit to them has no effect. Point them at rules.
  ...orderEditingGroup("order_editing.editing_rules", {
    "orderEditing/tools/cancelOrder/restockInventory": setting({
      label: "Restock inventory",
      description:
        "Return items to available inventory when the order is cancelled",
      synonyms: [
        "restock inventory on cancel",
        "return inventory when cancelling order",
        "put inventory back on cancel",
        "restock cancelled order items",
      ],
      valueSemantics: restockValueSemantics,
    }),
    "orderEditing/tools/updateOrder/removeItemRestockInventory": setting({
      label: "Restock inventory",
      description:
        "Return items to available inventory when they are removed from the order",
      synonyms: [
        "restock inventory when removing items",
        "return inventory when removing order items",
        "put inventory back when removing items",
        "restock removed order items",
      ],
      valueSemantics: restockValueSemantics,
    }),
    "orderEditing/tools/updateOrder/changeVariantRestockInventory": setting({
      label: "Restock inventory",
      description:
        "Return the original variant to available inventory when it is swapped",
      synonyms: [
        "restock inventory when changing variant",
        "return original variant to inventory",
        "put original variant back in stock",
        "restock swapped variant",
      ],
      valueSemantics: restockValueSemantics,
    }),
  }),
};

type OrderEditingFlatTeamSettingKey = keyof typeof OrderEditingRegistry;
const OrderEditingFlatTeamSettingKeys = Object.keys(
  OrderEditingRegistry,
) as OrderEditingFlatTeamSettingKey[];

export const OrderEditingFlatTeamSettingCatalogDefinitions =
  OrderEditingFlatTeamSettingKeys.map((key) => {
    const definition = OrderEditingRegistry[key];
    return {
      id: key,
      groupId: definition.group,
      metadata: createSettingMetadataFromGroupDefaults({
        setting: definition,
        group: OrderEditingFlatTeamSettingGroupById[definition.group],
      }),
      valueSource: { adapter: "teamSettings", key },
    };
  }) satisfies readonly SettingDefinition<
    OrderEditingFlatTeamSettingKey,
    OrderEditingFlatTeamSettingGroupId,
    TeamSettingsSettingValueSource<OrderEditingFlatTeamSettingKey>
  >[];

export const OrderEditingFlatTeamSettingMetadataByKey = Object.fromEntries(
  OrderEditingFlatTeamSettingCatalogDefinitions.map((definition) => [
    definition.id,
    definition.metadata,
  ]),
) as Record<OrderEditingFlatTeamSettingKey, SettingMetadata>;
